import type { DesignData, MediaElement } from 'editor/src/store/design/types';

import PrintableContentError from './PrintableContentError';

const hasInk = (colour: string | undefined) =>
  Boolean(colour && colour !== 'none' && colour !== 'transparent' && !/^#[0-9a-f]{6}00$/i.test(colour));

/** Classifies exported design data, after conditions and gallery assets have been materialized. */
function isPrintable(media: MediaElement): boolean {
  if (media.hidden) {
    return false;
  }
  switch (media.type) {
    case 'image':
      if (media.uploading || media.digitizing) {
        throw new PrintableContentError('Artwork processing is incomplete.');
      }
      return Boolean(media.imageId && media.url && media.width > 0 && media.height > 0);
    case 'addon':
      // Same guard as images: the order-import classifier rejects either state, so accepting a
      // digitizing addon here would price an area the import then declines.
      if (media.uploading || media.digitizing) {
        throw new PrintableContentError('Artwork processing is incomplete.');
      }
      // Addons resolve their artwork from the addon catalogue and carry no gallery asset id.
      return Boolean(media.addonId && media.url && media.width > 0 && media.height > 0);
    case 'text':
      return Boolean(media.extra.text.trim() && media.width > 0 && media.height > 0);
    case 'line':
      return media.strokeWidth > 0 && hasInk(media.stroke) && (media.x1 !== media.x2 || media.y1 !== media.y2);
    case 'rectangle':
      return (
        media.width > 0 && media.height > 0 && (hasInk(media.fill) || (media.strokeWidth > 0 && hasInk(media.stroke)))
      );
    default:
      // Calendar grids only reach apparel through an unsupported template. Unsupported output
      // must not be silently treated as an empty/free print area.
      throw new PrintableContentError(`Unsupported printable media: ${media.type}`);
  }
}

export default function classifyPrintableAreas(
  design: DesignData,
  areaAliases: Record<string, string>,
  allowedAreaIds: string[],
): string[] {
  const populated = new Set<string>();
  design.spreads.forEach((spread) => {
    let prints = false;
    spread.pages.forEach((page) => {
      if (hasInk(page.backgroundColor)) {
        prints = true;
      }
      // Always inspect all elements, even after finding ink, so unsupported output cannot hide behind it.
      (page.groups.media || []).forEach((media) => {
        prints = isPrintable(media) || prints;
      });
    });
    if (prints) {
      const area = areaAliases[spread.name];
      if (!area || !allowedAreaIds.includes(area)) {
        throw new PrintableContentError('Artwork uses an unavailable print area.');
      }
      populated.add(area);
    }
  });
  return [...populated].sort();
}
